import React from "react";
import { Link } from "react-router-dom";

import { Clock, Cog, Truck } from "../../../components/icons/svg/outline";
import {
  getProjectCustomerLabel,
  getProjectTitle,
} from "../../../utils/business";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
} from "../shared/projectTicketSummaries";
import {
  ProjectRfiSummaryTile,
  ProjectTmSummaryTile,
} from "./CompactTicketSummary";
import {
  emptyProjectCardOpsSummary,
  formatProjectCardMetric,
  getProjectStatusConfig,
  PROJECT_HOME_CHIP_CLASS,
} from "./projectHomeHelpers";

/**
 * Build the card subtitle: customer name plus manager first names.
 *
 * @param {Object} project - Project API row.
 * @returns {string}
 */
function getProjectCardCustomerManagerLabel(project) {
  const parts = [];
  const customerName = project?.customer?.name;
  if (customerName) {
    parts.push(customerName);
  }
  for (const manager of project?.managers || []) {
    const firstName = String(manager?.first || "").trim();
    if (firstName) {
      parts.push(firstName);
    }
  }
  return parts.join(", ") || getProjectCustomerLabel(project);
}

/**
 * Whether any concrete/labor amount is non-zero.
 *
 * @param {Object} opsSummary - Brief ops summary for a project card.
 * @returns {boolean}
 */
function hasAnyOpsMetric(opsSummary) {
  return (
    Number(opsSummary.concreteToday) > 0 ||
    Number(opsSummary.concrete7d) > 0 ||
    Number(opsSummary.laborToday) > 0 ||
    Number(opsSummary.labor7d) > 0
  );
}

/**
 * One concrete or labor column inside the shared ops panel.
 *
 * @param {Object} props
 * @param {React.ComponentType<{className?: string}>} props.Icon - Leading icon.
 * @param {string} props.label - Column label (e.g. ``Concrete``).
 * @param {string} [props.title] - Optional native tooltip for the column.
 * @param {Array<{periodLabel: string, value: string, unit: string, title?: string}>} props.metrics - Visible metrics.
 * @param {boolean} [props.isLoading=false] - Whether ops metrics are loading.
 * @returns {JSX.Element}
 */
function OpsMetricColumn({ Icon, label, title, metrics, isLoading = false }) {
  return (
    <div className="min-w-0 flex-1 px-3 py-2.5" title={title || undefined}>
      <div className="mb-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <div className="flex gap-3">
        {metrics.map((metric) => (
          <div
            key={`${metric.periodLabel}-${metric.unit}`}
            className="min-w-0 flex-1"
            title={metric.title || undefined}
          >
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              {metric.periodLabel}
            </div>
            <div className="mt-0.5 flex items-baseline gap-1">
              <span
                className={`tabular-nums leading-none text-gray-900 ${
                  !isLoading && metric.value === "-"
                    ? "text-base font-normal text-gray-400"
                    : "text-xl font-bold"
                }`}
              >
                {isLoading ? "…" : metric.value}
              </span>
              <span className="text-xs font-medium text-gray-400">
                {metric.unit}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Project summary card for the Projects landing page.
 *
 * Uses a real link so middle-click / ctrl-click open the project in a new tab.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.project - Project API row.
 * @param {(project: Object) => void} [props.onEdit] - Opens project edit modal.
 * @param {Object} [props.tmSummary] - Compact T&M status summary for this project.
 * @param {Object} [props.rfiSummary] - Compact RFI status summary for this project.
 * @param {boolean} [props.ticketsLoading=false] - Whether ticket lists are still loading.
 * @param {Object} [props.opsSummary] - Brief concrete + dispatch-estimated labor totals.
 * @param {boolean} [props.opsLoading=false] - Whether concrete/labor metrics are loading.
 * @param {boolean} [props.showZeroMetrics=false] - Show all-zero ops/ticket rows.
 * @returns {JSX.Element}
 */
const ProjectCard = ({
  project,
  onEdit,
  tmSummary = emptyTmCardSummary(),
  rfiSummary = emptyRfiCardSummary(),
  ticketsLoading = false,
  opsSummary = emptyProjectCardOpsSummary(),
  opsLoading = false,
  showZeroMetrics = false,
}) => {
  const status = getProjectStatusConfig(project, "header");
  const projectNumber =
    project.number || project.project_number || `Project ${project.id}`;
  const projectTitle = getProjectTitle(project);
  const customerManagerLabel = getProjectCardCustomerManagerLabel(project);
  const projectPath = `/projects/${project.id}`;
  const cardChipClassName = `${PROJECT_HOME_CHIP_CLASS} h-5 text-xs`;

  // Any non-zero ops value reveals the full concrete + labor panels together.
  const showOpsRow =
    showZeroMetrics || opsLoading || hasAnyOpsMetric(opsSummary);
  const concreteMetrics = [
    {
      periodLabel: "Today",
      value: formatProjectCardMetric(opsSummary.concreteToday),
      unit: "CY",
    },
    {
      periodLabel: "7 Days",
      value: formatProjectCardMetric(opsSummary.concrete7d),
      unit: "CY",
    },
  ];
  const laborMetrics = [
    {
      periodLabel: "Today",
      value: formatProjectCardMetric(opsSummary.laborToday),
      unit: "hrs",
    },
    {
      periodLabel: "7 Days",
      value: formatProjectCardMetric(opsSummary.labor7d),
      unit: "hrs",
    },
  ];
  const showTicketRow =
    showZeroMetrics ||
    ticketsLoading ||
    Number(tmSummary.toSend) > 0 ||
    Number(tmSummary.toSign) > 0 ||
    Number(tmSummary.toPrice) > 0 ||
    Number(tmSummary.toSubmit) > 0 ||
    Number(rfiSummary.toSend) > 0 ||
    Number(rfiSummary.toClose) > 0;

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-200/80 transition duration-200 hover:border-gray-300 hover:shadow-md hover:shadow-gray-300/70">
      {/* Real href so middle-click / open-in-new-tab work natively. */}
      <Link
        to={projectPath}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`View ${projectTitle}`}
      />

      <div className="relative z-10 flex flex-1 flex-col pointer-events-none">
        <div className="flex flex-col gap-1 px-4 pt-3.5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 truncate text-xs font-medium tabular-nums leading-none text-gray-400">
              {projectNumber}
            </div>
            <div
              className="min-w-0 max-w-[60%] truncate text-right text-xs font-normal text-gray-500"
              title={customerManagerLabel}
            >
              {customerManagerLabel}
            </div>
          </div>

          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 truncate text-[15px] font-bold leading-snug tracking-tight text-gray-900">
              {projectTitle}
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {!status.isActive ? (
                <span
                  className={`${cardChipClassName} whitespace-nowrap ${status.className}`}
                >
                  Done
                </span>
              ) : null}
              {onEdit ? (
                <button
                  type="button"
                  className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary-100"
                  aria-label={`Edit ${projectTitle}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEdit(project);
                  }}
                >
                  <Cog className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {showOpsRow ? (
          <div className="mx-4 mt-1 flex overflow-hidden rounded-lg border border-gray-200">
            <OpsMetricColumn
              Icon={Clock}
              label="Labor"
              title="Estimated from dispatch (8 hrs per assigned worker)"
              metrics={laborMetrics}
              isLoading={opsLoading}
            />
            <div className="w-px flex-shrink-0 bg-gray-200" aria-hidden />
            <OpsMetricColumn
              Icon={Truck}
              label="Concrete"
              metrics={concreteMetrics}
              isLoading={opsLoading}
            />
          </div>
        ) : null}

        {showTicketRow ? (
          <div className="mt-3 flex flex-col gap-2 px-4 pb-3.5">
            <ProjectTmSummaryTile
              summary={tmSummary}
              isLoading={ticketsLoading}
              showZeroMetrics={showZeroMetrics}
            />
            <ProjectRfiSummaryTile
              summary={rfiSummary}
              isLoading={ticketsLoading}
              showZeroMetrics={showZeroMetrics}
            />
          </div>
        ) : (
          <div className="pb-3.5" />
        )}
      </div>
    </article>
  );
};

export default ProjectCard;
